import { AppContext } from 'context/app';
import type { PathLocation, Site } from 'context/types';
import { useContext, useEffect, useMemo } from 'react';
import { ensureHttps } from 'utils/url';

import { useBranding } from './useBranding';
import { useLandmarks } from './useLandmarks';
import { useNetwork } from './useNetwork';
import { usePlaces } from './usePlaces';
import { useSatellitePlaces } from './useSatellitePlaces';
import { useSitePath } from './useSitePath';

export const useNetworkManager = () => {
  const clientName = process.env.REACT_APP_CUSTOMER_ENV!;
  const kioskMode = process.env.REACT_APP_KIOSK_MODE === 'true';

  const {
    selectedSite,
    setCampusName,
    setAvailableSites,
    setSelectedSite,
    availableSites,
  } = useContext(AppContext);

  const { data: networkData, isLoading: isNetworkLoading } =
    useNetwork(clientName);

  const { data: brandingData, isLoading: isBrandingLoading } =
    useBranding(clientName);

  const { data: placesData, isLoading: placesDataLoading } =
    usePlaces(clientName);

  const { data: landmarksData, isLoading: landmarksLoading } =
    useLandmarks(clientName);

  const { data: siteData } = useSitePath(
    clientName,
    selectedSite || (availableSites ? availableSites[0] : undefined),
  );

  const { data: satellitePlacesData, isLoading: isSatellitePlacesDataLoading } =
    useSatellitePlaces(clientName);

  const placesWithCoordinates = useMemo(
    () =>
      placesData?.data?.map((place) => {
        const position =
          siteData?.data.nodes.find(
            (l: PathLocation) => l.k === place.location.id,
          ) ??
          siteData?.data.locations.find(
            (l: PathLocation) => l.k === place.location.id,
          );

        return {
          ...place,
          x: position?.x ?? 0,
          y: position?.y ?? 0,
        };
      }),
    [placesData?.data, siteData?.data.locations, siteData?.data.nodes],
  );

  const prioritizedPlaces = useMemo(
    () =>
      placesWithCoordinates?.sort(
        ({ priority: a }, { priority: b }) => b - a,
      ) ?? [],
    [placesWithCoordinates],
  );

  useEffect(() => {
    setCampusName(selectedSite?.short_name ?? selectedSite?.name ?? '');
  }, [setCampusName, selectedSite]);

  useEffect(() => {
    if (networkData) {
      const sitesFromNetwork = networkData.data.sites;
      const modifiedTempData = [...sitesFromNetwork];
      setAvailableSites(modifiedTempData);
      if (kioskMode) {
        setSelectedSite(networkData.data.sites[0]);
      }
    }
  }, [kioskMode, networkData, setAvailableSites, setSelectedSite]);

  useEffect(() => {
    if (networkData?.data) {
      const currentSite: Site | undefined =
        selectedSite ?? networkData?.data.sites[0];
      const siteName: string | undefined =
        currentSite?.short_name ?? currentSite?.name;
      document.title = `
        ${
          networkData?.data &&
          (networkData.data.display_name || networkData.data.name)
        }${siteName && ` | ${siteName}`} 
        ${(kioskMode && ' - KIOSK') || ''}
      `;
    }
  }, [networkData, kioskMode, selectedSite]);

  const siteName = useMemo(
    () => networkData?.data.display_name || networkData?.data.name,
    [networkData],
  );

  const siteLogo = useMemo(
    () => ensureHttps(brandingData?.data[0]?.landing_footer_logo_url),
    [brandingData],
  );

  const callToActionImage = useMemo(
    () => ensureHttps(brandingData?.data[0]?.install_app_screen_image),
    [brandingData],
  );

  return {
    networkData: networkData?.data,
    isNetworkLoading,
    brandingData: brandingData?.data,
    isBrandingLoading,
    prioritizedPlaces,
    placesData: placesWithCoordinates,
    placesDataLoading,
    landmarksData: landmarksData?.data,
    landmarksLoading,
    siteName,
    siteLogo,
    clientName,
    satellitePlacesData,
    isSatellitePlacesDataLoading,
    callToActionImage,
  };
};
